import React, { useState, useEffect, useCallback } from 'react';
import { Table, notification, Dropdown, Button, Space } from 'antd';
import { formatPrice, formatDate } from '../../../../../utils/format';
import { apiDepositTransactionsList, apiDepositTransactionsDownload } from '../../../../../api/deposits';
import { Link } from "react-router-dom";
import { InfoCircleOutlined, FileDoneOutlined, DownOutlined } from "@ant-design/icons";

const DepositTransactionsList = ({ depositId }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchTransactions = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const filters = {
        page: params.page || 1,
        page_size: params.pageSize || 10,
      };

      const response = await apiDepositTransactionsList(depositId, filters);
      setData(response.results || []);
      setPagination(prev => ({
        ...prev,
        total: response.count || 0,
        current: params.page || 1,
      }));
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao buscar as transações relacionadas.",
        placement: "topRight",
      });
    } finally {
      setLoading(false);
    }
  }, [depositId]);

  useEffect(() => {
    if (depositId) {
      fetchTransactions();
    }
  }, [depositId, fetchTransactions]);

  const handleTableChange = (pagination) => {
    fetchTransactions({
      page: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const handleDownload = async (format) => {
    setDownloading(true);
    try {
      const blob = await apiDepositTransactionsDownload(format, depositId, {});
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Transacoes_do_deposito.${format === 'csv' ? 'csv' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download file:', error);
    } finally {
      setDownloading(false);
    }
  };

  const items = [
    {
      label: 'CSV',
      key: 'csv',
      icon: <FileDoneOutlined />,
    },
    {
      label: 'EXCEL',
      key: 'excel',
      icon: <FileDoneOutlined />,
    },
  ];

  const handleMenuClick = (e) => {
    handleDownload(e.key);
  };

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  const columns = [
    {
      title: 'ID da Transação',
      dataIndex: 'transaction',
      key: 'transaction',
      render: (transactionId) => (
        <Link to={`/transacao/${transactionId}`} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {transactionId}
          <InfoCircleOutlined style={{ color: '#1890ff' }} />
        </Link>
      ),
    },
    {
      title: 'Valor',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => formatPrice(amount),
    },
    {
      title: 'Conta Recebedora',
      dataIndex: ['receiver_account', 'display_name'],
      key: 'receiver_account',
    },
    {
      title: 'Conta Adquirente',
      dataIndex: ['acquirer_account', 'display_name'],
      key: 'acquirer_account',
    },
    {
      title: 'Data de Criação',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => date ? formatDate(date) : 'N/A',
    },
    {
      title: 'Última Atualização',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date) => date ? formatDate(date) : 'N/A',
    },
  ];

  if (!data.length && !loading) {
    return <p>Nenhuma transação relacionada encontrada.</p>;
  }

  return (
    <>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          current: pagination.current,
          total: pagination.total,
          pageSize: pagination.pageSize,
          showSizeChanger: false,
        }}
        onChange={handleTableChange}
        bordered
        rowKey={(record) => record.id}
        scroll={{ x: 800 }}
      />
      <div className="download-section">
        <Dropdown menu={menuProps} >
          <Button loading={downloading}>
            <Space>
              Baixar Relatório
              <DownOutlined />
            </Space>
          </Button>
        </Dropdown>
      </div>
    </>
  );
};

export default DepositTransactionsList;
